import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormatPipe } from '../../../pipes/format.pipe';
import { SidekickService } from '../../../services/sidekick/sidekick.service';
import { BaseComponent } from '../../base/base.component';
import { MoreButtonComponent } from '../../core/buttons/more-button.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import type { LoadingState } from '../../core/panels/loading-panel.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-ai-summary-card',
  standalone: true,
  imports: [
    CommonModule,
    ActionRowComponent,
    MoreButtonComponent,
    FormatPipe,
    LoadingPanelComponent,
    EmptyStateComponent,
  ],
  template: `
    <app-loading-panel
      [state]="loadingState"
      loadingStyle="Template"
      [error]="error"
    >
      <ng-template #loading>
        <div class="tw-animate-pulse tw-flex tw-flex-col tw-items-center">
          <app-empty-state
            icon="fa-light fa-stars"
            label="Retrieving summary"
            height="300px"
          >
          </app-empty-state>
        </div>
      </ng-template>
      <ng-template #loaded>
        <div class="tw-w-full ">
          @if (createdDate) {
            <div
              class="tw-w-full tw-pb-3 tw-flex tw-flex-row-reverse"
              tabindex="0"
              [attr.aria-label]="
                'Summary created: ' + (createdDate | format: 'dt')
              "
            >
              <div
                class="tw-font-bold tw-text-[1rem] tw-text-[var(--sed-black-60-color)]"
              >
                Created: {{ createdDate | format: 'dt' }}
              </div>
            </div>
          }
          @if (claimStatus) {
            <div tabindex="0" role="article" aria-label="Claim status summary">
              <p class="tw-whitespace-pre-line">
                {{ claimStatus }}
              </p>
            </div>
          }
          @if (summaryOfActions) {
            <div tabindex="0" role="article" aria-label="Summary of actions">
              <p class="tw-whitespace-pre-line">
                {{ summaryOfActions }}
              </p>
            </div>
          }
          <div class="tw-pt-2 tw-pb-4" tabindex="0" aria-label="AI disclaimer">
            <i
              >This content was generated by AI and may contain errors or
              inaccuracies.</i
            >
          </div>
          @if (
            (this.claimSummaryAuth && this.claimSummaryIncurredAuth) ||
            this.propertyToken
          ) {
            <app-action-row>
              <app-more-button
                right1
                (click)="this.view.navigateCurrentClaim(['sidekick-summary'])"
                [ariaLabel]="'Navigate to ai summary'"
              />
            </app-action-row>
          }
        </div>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class AiSummaryCardComponent extends BaseComponent implements OnInit {
  private sidekickService = inject(SidekickService);

  loadingState: LoadingState = 'Loading';
  error: any = null;
  claimStatus = '';
  summaryOfActions = '';
  createdDate: Date | null = null;
  isWC = false;
  hasData = false;
  claimSummaryAuth = false;
  claimSummaryIncurredAuth = false;
  propertyToken = false;

  @HandleLoadingState({
    loadingProperty: 'loadingState',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  async ngOnInit(): Promise<void> {
    this.claimSummaryAuth = this.session.authorize('ClaimSummary');
    this.claimSummaryIncurredAuth = this.session.authorize('ClaimSummaryIncurred');
    this.propertyToken = this.session.authorize('Property');

    const claim = this.view.currentClaim();
    if (claim.lineOfBusiness === 'WC') {
      this.isWC = true;

      try {
        const wcSummary = await this.sidekickService.getWCSummaryCard(
          claim.recordSource,
          claim.recordId,
        );

        this.claimStatus = wcSummary.claimStatus || '';
        this.summaryOfActions = wcSummary.summaryOfActions || '';
        this.createdDate = wcSummary.createdDate
          ? new Date(wcSummary.createdDate)
          : null;

        this.hasData = !!(this.claimStatus || this.summaryOfActions);
      } catch (error) {
        this.hasData = false;
      }
    } else {
      const summary = await this.sidekickService
        .getSummaryCard(claim.recordSource, claim.recordId)
        .catch((): null => null);

      if (summary) {
        this.claimStatus = summary.claimStatus || '';
        this.summaryOfActions = summary.summaryOfActions || '';
        this.createdDate = summary.createdDate
          ? new Date(summary.createdDate)
          : null;
        this.hasData = !!(this.claimStatus || this.summaryOfActions);
      }
    }
  }
}
